'use client';

import { createContext, type RefObject, use } from 'react';

/**
 * Surface for any descendant of {@link SDSInternalDialog} to discover the
 * dialog's "popover container" — a stable div rendered beside the
 * Dialog popup inside the same portal container.
 *
 * Why this exists: Base UI's `Dialog` (modal=true by default) and Base UI's
 * `Popover` (modal=false by default) each mount their own body-level
 * `FloatingPortal`. When both are open, the popover lands as a body sibling
 * of the dialog backdrop and is rendered invisibly. Routing the popover's
 * portal into the dialog's sibling slot keeps it within the dialog's focus
 * scope and stacking context without clipping against the popup.
 *
 * Default value is `null` so consumers can reliably detect "no surrounding
 * dialog" and fall back to the regular body portal.
 */
export interface DialogPortalContextValue {
  portalContainerRef: RefObject<HTMLElement | null>;
}

export const DialogPortalContext = createContext<DialogPortalContextValue | null>(null);

export function useDialogPortalContext(): DialogPortalContextValue | null {
  return use(DialogPortalContext);
}
